import type { ContentCardProps } from '../../types';
import { Body, Card, Col, Icon, Span, Stack } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';
import { getIconColor } from '~/utils';
import { CardOverflowMenu } from '../OverflowCardMenu';
import { CardDescription, CardTitle } from '../CardText';
import { buildCardMetadata } from '../../utils';
import { FooterWrapper, FullHeightRow } from '../../styles';

export interface ResourceCardProps extends ContentCardProps {
  viewed?: boolean | null;
  onPress?: () => void;
}

const { DEFAULT_COVER_IMAGES } = window.APP_CONSTANTS;

export const ResourceCard = ({
  name,
  imageSrc,
  description,
  viewed,
  onPress,
  tags = [],
  skills = [],
  overflowMenuItems = {},
  hideContentCTA = false,
  actions,
}: ResourceCardProps) => {
  const VIEWED = I18n.t('js.learner.libraries.viewed');
  const VIEW = I18n.t('js.learner.card.view');
  const theme = useTheme();

  const metadata = buildCardMetadata(null, skills, tags, theme.colors.on[600]);

  const showOverflowMenu = Boolean(Object.keys(overflowMenuItems || {}).length);
  const showViewedStatus = !hideContentCTA;

  const iconColor = getIconColor(theme.colors.decorative.teal[500]);

  return (
    <Card onPress={onPress}>
      {imageSrc && (
        <Card.Hero
          src={imageSrc}
          errorImage={DEFAULT_COVER_IMAGES.RESOURCE}
          icon={<Icon color={iconColor} name="resource:regular" size="lg" />}
        />
      )}
      <Card.Container data-testid="library-card">
        <Card.Content>
          <Card.Body>
            <Stack size={4}>
              {!imageSrc && <Card.Icon iconColor={iconColor} iconName="resource:regular" />}
              <Stack size={2}>
                <CardTitle text={name} />
                {metadata.length ? <Card.Metadata metadata={metadata} /> : null}
                <CardDescription text={description} color={theme.colors.on[500]} />
              </Stack>
            </Stack>
          </Card.Body>
        </Card.Content>
      </Card.Container>
      {(viewed || showOverflowMenu || actions) && (
        <Card.Footer size={0}>
          <FooterWrapper>
            <FullHeightRow align="center" justify={showViewedStatus ? 'space-between' : 'flex-end'}>
              {showViewedStatus && (
                <Col>
                  <Body color={theme.colors.on[600]} level="md" unselectable="on">
                    {viewed && (
                      <Span>
                        <Icon name="check-circle:regular" size="md" /> {VIEWED}
                      </Span>
                    )}
                    {!viewed && <Span>{VIEW}</Span>}
                  </Body>
                </Col>
              )}
              {(showOverflowMenu || actions) && (
                <Col xs="auto">
                  {actions}
                  {showOverflowMenu && <CardOverflowMenu overflowMenuItems={overflowMenuItems} />}
                </Col>
              )}
            </FullHeightRow>
          </FooterWrapper>
        </Card.Footer>
      )}
    </Card>
  );
};
